import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { Appointment, AppointmentType, AppointmentStatus } from '../../types/appointment';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { createEncounter, getEncounterForAppointmentAndPet } from '../../utils/encounterApi';

interface AppointmentViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: Appointment | null;
  onEdit: () => void;
  onDelete: () => void;
}

const AppointmentViewModal: React.FC<AppointmentViewModalProps> = ({
  isOpen,
  onClose,
  appointment,
  onEdit,
  onDelete,
}) => {
  const navigate = useNavigate();
  const [petOwner, setPetOwner] = useState<any>(null);
  const [assignedVet, setAssignedVet] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [encounterLoading, setEncounterLoading] = useState(false);

  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    if (isOpen && appointment) {
      console.log('🔍 [AppointmentViewModal] Modal opened:', {
        id: appointment.id,
        title: appointment.title,
        status: appointment.status,
        statusType: typeof appointment.status,
        IN_PROGRESS_ENUM: AppointmentStatus.IN_PROGRESS,
        matches: appointment.status === AppointmentStatus.IN_PROGRESS
      });
      fetchRelatedData();
    }
  }, [isOpen, appointment]);

  const fetchRelatedData = async () => {
    if (!appointment) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const baseURL = API_BASE_URL;

      // Fetch pet owner details
      if (appointment.pet_owner_id) {
        try {
          const ownerResponse = await fetch(`${baseURL}/api/v1/pet-owners/${appointment.pet_owner_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (ownerResponse.ok) {
            const ownerData = await ownerResponse.json();
            setPetOwner(ownerData);
          }
        } catch (err) {
          console.error('Failed to fetch pet owner:', err);
        }
      }

      // Fetch assigned vet details
      if (appointment.assigned_vet_user_id) {
        try {
          const vetResponse = await fetch(`${baseURL}/api/v1/users/${appointment.assigned_vet_user_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (vetResponse.ok) {
            const vetData = await vetResponse.json();
            setAssignedVet(vetData);
          }
        } catch (err) {
          console.error('Failed to fetch vet:', err);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !appointment) return null;

  const getAppointmentTypeLabel = (type: AppointmentType) => {
    switch (type) {
      case AppointmentType.CHECKUP:
        return 'Checkup';
      case AppointmentType.SURGERY:
        return 'Surgery';
      case AppointmentType.EMERGENCY:
        return 'Emergency';
      case AppointmentType.CONSULTATION:
        return 'Consultation';
      default:
        return type;
    }
  };

  const getStatusLabel = (status: AppointmentStatus) => {
    switch (status) {
      case AppointmentStatus.SCHEDULED:
        return 'Scheduled';
      case AppointmentStatus.CONFIRMED:
        return 'Confirmed';
      case AppointmentStatus.IN_PROGRESS:
        return 'In Progress';
      case AppointmentStatus.COMPLETE:
      case AppointmentStatus.COMPLETED:
        return 'Completed';
      case AppointmentStatus.CANCELLED:
        return 'Cancelled';
      case AppointmentStatus.NO_SHOW:
        return 'No Show';
      default:
        return status;
    }
  };

  const formatDateTime = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      return format(date, 'EEEE, MMMM d, yyyy \'at\' h:mm a');
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      return format(date, 'h:mm a');
    } catch {
      return dateStr;
    }
  };

  const calculateEndTime = (startDate: string, durationMinutes: number) => {
    try {
      const start = parseISO(startDate);
      const end = new Date(start.getTime() + durationMinutes * 60000);
      return format(end, 'h:mm a');
    } catch {
      return '';
    }
  };

  const handlePetClick = async (petId: string) => {
    if (!appointment) return;
    
    setEncounterLoading(true);
    try {
      // Check if encounter already exists for this appointment and pet
      let encounter;
      try {
        encounter = await getEncounterForAppointmentAndPet(appointment.id, petId);
      } catch (err: any) {
        // Encounter doesn't exist (404), create it
        if (err.status === 404 || err.name === 'EncounterAPIError') {
          encounter = await createEncounter(petId, {
            appointment_id: appointment.id,
            pet_id: petId,
            owner_id: appointment.pet_owner_id,
            practice_id: appointment.practice_id,
          });
        } else {
          throw err;
        }
      }
      
      // Navigate to encounter page with new URL structure
      onClose();
      navigate(`/pet/${petId}/encounter/${encounter.id}/intake`);
    } catch (err: any) {
      console.error('Failed to start encounter:', err);
      alert(err.message || err.detail || 'Failed to start encounter');
    } finally {
      setEncounterLoading(false);
    }
  };

  return createPortal(
    <>
      {/* Backdrop - covers entire screen and blocks clicks */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[400]"
        onClick={onClose}
      />
      
      {/* Modal - Google Calendar Style */}
      <div className="fixed inset-0 flex items-center justify-center z-[500] p-4">
        <div 
          className="bg-white rounded-lg shadow-2xl max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with Avatar and Icons */}
          <div className="relative px-6 pt-6 pb-4">
            {/* Top Icons Row */}
            <div className="absolute top-4 right-4 flex items-center space-x-2">
              {/* Edit */}
              <button
                onClick={onEdit}
                className="p-2 rounded-full hover:bg-blue-50 transition-colors"
                title="Edit"
              >
                <svg className="w-5 h-5 text-gray-600 hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
              
              {/* Delete */}
              <button
                onClick={onDelete}
                className="p-2 rounded-full hover:bg-red-50 transition-colors"
                title="Delete"
              >
                <svg className="w-5 h-5 text-gray-600 hover:text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
              
              {/* Close */}
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                title="Close"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Avatar/Icon */}
            <div className="flex items-center space-x-3 mb-4 pr-24">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                </svg>
              </div>
            </div>

            {/* Title and Date/Time - Single clickable area */}
            <div 
              className="px-2 py-1 -mx-2 rounded cursor-pointer hover:bg-gray-100 transition-colors group"
              onClick={onEdit}
              title="Click to edit"
            >
              <div className="flex items-start justify-between">
                <h2 className="text-2xl font-normal text-gray-900">
                  {appointment.title || getAppointmentTypeLabel(appointment.appointment_type)}
                </h2>
                {/* Edit icon - shows on hover */}
                <svg className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {formatDateTime(appointment.appointment_date)}
              </p>
            </div>
          </div>

          {/* Content - Google Calendar Style */}
          <div className="px-6 pb-6 space-y-4">
            {/* Time - Clickable to Edit */}
            <div 
              className="flex items-start space-x-4 -mx-2 px-2 py-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors group"
              onClick={onEdit}
              title="Click to edit time"
            >
              <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1 flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-900">
                    {formatTime(appointment.appointment_date)} – {calculateEndTime(appointment.appointment_date, appointment.duration_minutes)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {appointment.duration_minutes} minutes
                  </p>
                </div>
                {/* Edit icon - shows on hover */}
                <svg className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity ml-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
            </div>

            {/* Pets - Clickable to start encounters */}
            {appointment.pets && appointment.pets.length > 0 && (
              <div className="flex items-start space-x-4">
                <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <div className="space-y-2">
                    {appointment.pets.map((pet) => (
                      <div 
                        key={pet.id} 
                        className="flex items-center space-x-2 -mx-2 px-2 py-1 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handlePetClick(pet.id)}
                        title="Click to start/view encounter"
                      >
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-3.5 h-3.5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M4.5 12.75a2.25 2.25 0 114.5 0 2.25 2.25 0 01-4.5 0zM15 12.75a2.25 2.25 0 114.5 0 2.25 2.25 0 01-4.5 0zM8.25 18a2.25 2.25 0 114.5 0 2.25 2.25 0 01-4.5 0zM4.5 6.75a2.25 2.25 0 114.5 0 2.25 2.25 0 01-4.5 0zM15 6.75a2.25 2.25 0 114.5 0 2.25 2.25 0 01-4.5 0z"/>
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{pet.name}</p>
                          {pet.species && (
                            <p className="text-xs text-gray-500">
                              {pet.species}{pet.breed && ` • ${pet.breed}`}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Pet Owner */}
            {petOwner && (
              <div className="flex items-start space-x-4">
                <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{petOwner.full_name}</p>
                  {petOwner.phone && (
                    <p className="text-xs text-gray-500">{petOwner.phone}</p>
                  )}
                </div>
              </div>
            )}

            {/* Assigned Vet */}
            {assignedVet && (
              <div className="flex items-start space-x-4">
                <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{assignedVet.full_name || assignedVet.email}</p>
                </div>
              </div>
            )}

            {/* Description/Notes */}
            {appointment.description && (
              <div className="flex items-start space-x-4">
                <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{appointment.description}</p>
                </div>
              </div>
            )}

            {/* Type */}
            <div className="flex items-start space-x-4">
              <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-gray-900">{getAppointmentTypeLabel(appointment.appointment_type)}</p>
              </div>
            </div>

            {/* Status */}
            <div className="flex items-start space-x-4">
              <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-gray-900">{getStatusLabel(appointment.status)}</p>
              </div>
            </div>

            {/* Appointment */}
            <div className="flex items-start space-x-4">
              <svg className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-gray-900">Appointment</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default AppointmentViewModal;

